import { useCallback, useState, useEffect } from "react";
import ConfirmDialog from "src/components/dialog/ConfirmDialog";
import { api } from "src/api/endpoints";
import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { loginFlagOff, passwordChange, changePassword } from "../../redux/actions/User";
import toast from "react-hot-toast";
import Circularbar from "../../components/circularbar/Circularbar";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { generatePass } from "src/utils/generate-pass";

export const CustomerPasswordEdit = ({ user, role }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isCurrentPasswordInvalid = useSelector((state) => state.User.isCurrentPasswordInvalid);
  const params = useParams();

  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const [showConfirmPasswordError, setShowConfirmPasswordError] = useState(false);
  const [showConfirmPasswordErrorText, setShowConfirmPasswordErrorText] = useState("");

  const [showPasswordError, setShowPasswordError] = useState(false);
  const [showPasswordErrorText, setShowPasswordErrorText] = useState("");

  const [showCurrentPasswordError, setShowCurrentPasswordError] = useState(false);
  const [showCurrentPasswordErrorText, setShowCurrentPasswordErrorText] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showNewPassAdmin, setShowNewPassAdmin] = useState(false)
  const [isADConfirmOpen, setIsADConfirmOpen] = useState(false)

  const [currentADnewPass, setCurrentADnewPass] = useState('')

  useEffect(() => {
    if (isCurrentPasswordInvalid) {
      setShowCurrentPasswordError(true);
      setShowCurrentPasswordErrorText(t("views.customerPasswordEdit.currentPasswordInvalid"));
    } else {
      setShowCurrentPasswordError(false);
      setShowCurrentPasswordErrorText("");
    }
  }, [isCurrentPasswordInvalid, t]);

  const passwordSaveChanges = useCallback(() => {
    let error = false;
    if (password !== confirmPassword) {
      setShowConfirmPasswordErrorText(t("views.customerPasswordEdit.passwordsDoNotMatch"));
      setShowConfirmPasswordError(true);
      error = true;
    }
    if (currentPassword === "") {
      setShowCurrentPasswordErrorText(t("views.customerPasswordEdit.currentPasswordEmpty"));
      setShowCurrentPasswordError(true);
      error = true;
    }
    if (password === "") {
      setShowPasswordErrorText(t("views.customerPasswordEdit.newPasswordEmpty"));
      setShowPasswordError(true);
      error = true;
    }
    if (confirmPassword === "") {
      setShowConfirmPasswordErrorText(t("views.customerPasswordEdit.confirmPasswordEmpty"));
      setShowConfirmPasswordError(true);
      error = true;
    }
    if (!error) {
      const passwords = {
        currentPassword: currentPassword,
        password: password,
        confirmPassword: confirmPassword,
      };

      dispatch(changePassword(user._id, passwords));
    }

    setConfirmOpen(false);
  }, [dispatch, currentPassword, password, confirmPassword, user._id, t]);

  const handleSetPassword = useCallback(
    (value) => {
      setShowPasswordErrorText("");
      setShowPasswordError(false);
      setPassword(value);
    },
    [dispatch]
  );

  const handleSetConfirmPassword = useCallback(
    (value) => {
      setShowConfirmPasswordError(false);
      setShowConfirmPasswordErrorText("");
      setConfirmPassword(value);
    },
    [dispatch]
  );

  const handleSetCurrentPassword = useCallback(
    (value) => {
      setShowCurrentPasswordError(false);
      setShowCurrentPasswordErrorText("");
      setCurrentPassword(value);
    },
    [dispatch]
  );

  const handleADPassChange = async () => {
    setIsADConfirmOpen(false)
    if(role == 'admin'){
      if(currentADnewPass.length >= 8){
        setCurrentADnewPass('')
        try{ 
          await api.post(`/user/${user._id}/changePassAD`, {userId: user._id, pass: currentADnewPass})
          toast.success("The user's password was successfully changed")
        }catch(error){
          toast.error("An error occurred while changing the password")
        }
      }else{
        toast.error('The password must be at least 5 characters long')
      }
    }
  }

  const handleGenerateSecureADpass = () => {
    setShowNewPassAdmin(true)
    setCurrentADnewPass(generatePass())
  }

  return (
    <>
      <Card sx={{
        border: user.role == 'admin' ? '1px solid #006AFF' : ''
      }}>
        <CardHeader title={t("views.customerPasswordEdit.changePassword")} />
        <CardContent>
          {role == 'user' ? (
            <>
              <Grid container>
                <Grid xs={12} md={6}>
                  <Stack spacing={3}>
                    <FormControl sx={{ width: "100%" }} variant={"standard"}>
                      <TextField
                        id="current-password"
                        required
                        placeholder={t("views.customerPasswordEdit.currentPassword")}
                        onChange={(e) => handleSetCurrentPassword(e.target.value)}
                        type={showCurrentPassword ? "text" : "password"}
                        variant="outlined"
                        autoComplete="off"
                        error={showCurrentPasswordError}
                        helperText={showCurrentPasswordErrorText}
                        InputProps={{
                          endAdornment: (
                            <IconButton aria-label="toggle password visibility" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                              {showCurrentPassword ? <VisibilityOff sx={{ color: "#551ffa" }} /> : <Visibility sx={{ color: "#551ffa" }} />}
                            </IconButton>
                          ),
                        }}
                      />
                    </FormControl>
    
                    <FormControl sx={{ width: "100%" }} variant={"standard"}>
                      <TextField
                        id="new-password"
                        required
                        placeholder={t("views.customerPasswordEdit.newPassword")}
                        onChange={(e) => handleSetPassword(e.target.value)}
                        type={showNewPassword ? "text" : "password"}
                        variant="outlined"
                        autoComplete="new-password"
                        error={showPasswordError}
                        helperText={showPasswordErrorText}
                        InputProps={{
                          endAdornment: (
                            <IconButton aria-label="toggle password visibility" onClick={() => setShowNewPassword(!showNewPassword)}>
                              {showNewPassword ? <VisibilityOff sx={{ color: "#551ffa" }} /> : <Visibility sx={{ color: "#551ffa" }} />}
                            </IconButton>
                          ),
                        }}
                      />
                    </FormControl>
    
                    <FormControl sx={{ width: "100%" }} variant={"standard"}>
                      <TextField
                        id="confirm-password"
                        required
                        placeholder={t("views.customerPasswordEdit.confirmPassword")}
                        onChange={(e) => handleSetConfirmPassword(e.target.value)}
                        type={showConfirmPassword ? "text" : "password"}
                        variant="outlined"
                        autoComplete="new-password"
                        error={showConfirmPasswordError}
                        helperText={showConfirmPasswordErrorText}
                        InputProps={{
                          endAdornment: (
                            <IconButton aria-label="toggle password visibility" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              {showConfirmPassword ? <VisibilityOff sx={{ color: "#551ffa" }} /> : <Visibility sx={{ color: "#551ffa" }} />}
                            </IconButton>
                          ),
                        }}
                      />
                    </FormControl>
                  </Stack>
                </Grid>
              </Grid>
              <Button onClick={() => setConfirmOpen(true)} variant="outlined" sx={{ marginTop: "20px" }}>
                {t("views.customerPasswordEdit.changePassword")}
              </Button>
            </>
          ) : (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              // justifyContent: 'space-between',
              gap: '20px'
            }}>
              <FormControl sx={{ width: "100%", maxWidth: '350px' }} variant={"standard"}>
                <TextField
                  required
                  placeholder={"Enter new user's password"}
                  onChange={(e) => handleSetConfirmPassword(e.target.value)}
                  type={showNewPassAdmin ? "text" : "password"}
                  variant="outlined"
                  autoComplete="off"
                  value={currentADnewPass}
                  onInput={(e) => setCurrentADnewPass(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton aria-label="toggle password visibility" onClick={() => setShowNewPassAdmin(!showNewPassAdmin)}>
                        {showNewPassAdmin ? <VisibilityOff sx={{ color: "#551ffa" }} /> : <Visibility sx={{ color: "#551ffa" }} />}
                      </IconButton>
                    ),
                  }}
                />
              </FormControl>
              <Button endIcon={<AutoAwesomeIcon />} variant="outlined" onClick={handleGenerateSecureADpass}>Generate strong password</Button>
              <Button variant="outlined" onClick={() => setIsADConfirmOpen(true)}>Change</Button>
            </Box>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog
        open={isConfirmOpen}
        title={t("views.customerPasswordEdit.confirmChangePassword")}
        onClose={() => setConfirmOpen(false)}
        onAccept={passwordSaveChanges}
      />
      <ConfirmDialog
        open={isADConfirmOpen}
        title={t("views.customerPasswordEdit.confirmChangePassword")}
        onClose={() => setIsADConfirmOpen(false)}
        onAccept={handleADPassChange}
      />
    </>
  );
};
